import { useEffect, useState } from 'react';

const THUMBNAIL_TARGET_WIDTH_PX = 240;

// Loaded from esm.sh at runtime rather than bundled as a local dependency:
// this app's Docker build stage runs on node:18-alpine, but pdfjs-dist
// versions with correct canvas-shading support (needed for some
// cover-letter/manuscript templates — older pdfjs-dist renders their
// gradient headers as solid pink) require Node >=20/22. Fetching it as a
// browser ES module sidesteps the build-time Node constraint entirely; only
// pages that actually render one of these thumbnails pay the ~1MB fetch.
const PDFJS_CDN_VERSION = '6.1.200';
const PDFJS_CDN_BASE = `https://esm.sh/pdfjs-dist@${PDFJS_CDN_VERSION}`;

let pdfjsLibPromise: Promise<typeof import('pdfjs-dist')> | undefined;

const loadPdfjs = () => {
  if (!pdfjsLibPromise) {
    pdfjsLibPromise = import(
      /* @vite-ignore */ PDFJS_CDN_BASE
    ) as Promise<typeof import('pdfjs-dist')>;
    pdfjsLibPromise.then((pdfjsLib) => {
      pdfjsLib.GlobalWorkerOptions.workerSrc = `${PDFJS_CDN_BASE}/build/pdf.worker.min.mjs`;
    });
  }
  return pdfjsLibPromise;
};

/**
 * Renders a PDF's first page to a PNG data URL entirely client-side: the
 * manuscript file has no server-generated thumbnail, unlike the book cover,
 * so the print shop ticket renders one in-browser via pdf.js instead of
 * reusing the cover art thumbnail. Requires the PDF's bucket to allow
 * cross-origin GET (pdf.js fetches the bytes, unlike a plain <a>/<img>).
 */
export const renderPdfFirstPageThumbnail = async (
  pdfUrl: string,
): Promise<string> => {
  const pdfjsLib = await loadPdfjs();

  const pdf = await pdfjsLib.getDocument({ url: pdfUrl }).promise;
  const page = await pdf.getPage(1);
  const baseViewport = page.getViewport({ scale: 1 });
  const scale = THUMBNAIL_TARGET_WIDTH_PX / baseViewport.width;
  const viewport = page.getViewport({ scale });

  const canvas = document.createElement('canvas');
  canvas.width = viewport.width;
  canvas.height = viewport.height;
  const context = canvas.getContext('2d');
  if (!context) {
    throw new Error('Canvas 2D context is unavailable');
  }

  await page.render({ canvasContext: context, viewport }).promise;
  return canvas.toDataURL('image/png');
};

/**
 * Shared loading/error state for a client-side PDF-first-page thumbnail
 * (the manuscript print-file card and the cover-letter example preview both
 * need it). `pdfUrl` of `undefined` skips rendering (e.g. no letter has
 * rendered yet), matching each caller's own "not ready" placeholder.
 */
export const usePdfFirstPageThumbnail = (
  pdfUrl: string | undefined,
): { src?: string; failed: boolean } => {
  const [src, setSrc] = useState<string>();
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setSrc(undefined);
    setFailed(false);
    if (!pdfUrl) return undefined;
    let cancelled = false;
    renderPdfFirstPageThumbnail(pdfUrl)
      .then((result) => {
        if (!cancelled) setSrc(result);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [pdfUrl]);

  return { src, failed };
};
